import { formatPercent } from './format.js';
import { CROSS_STATE_POSTAL_CODES, POSTAL_CODES_BY_STATE } from './postal-code-states.js';
/**
 * Federal state („Bundesland") and land transfer tax („Grunderwerbsteuer") of a property (owner decision
 * 12.10.2026, migration 0031): the tax rate is state law, so it follows the property's state — derived from the
 * postal code unless set by hand — and can be overridden per property (`land_transfer_tax_pct`, e.g. taken from
 * TeamProQ). Shared by the API (price card, investment calculator, importer) and the SPA (Objektdaten, edit dialog).
 */
/** The 16 Länder; the keys are the ISO 3166-2:DE suffixes in lower case (pgEnum `property_state`). */
export const PROPERTY_STATES = [
    'bw',
    'by',
    'be',
    'bb',
    'hb',
    'hh',
    'he',
    'mv',
    'ni',
    'nw',
    'rp',
    'sl',
    'sn',
    'st',
    'sh',
    'th',
];
export const propertyStateLabels = {
    bw: 'Baden-Württemberg',
    by: 'Bayern',
    be: 'Berlin',
    bb: 'Brandenburg',
    hb: 'Bremen',
    hh: 'Hamburg',
    he: 'Hessen',
    mv: 'Mecklenburg-Vorpommern',
    ni: 'Niedersachsen',
    nw: 'Nordrhein-Westfalen',
    rp: 'Rheinland-Pfalz',
    sl: 'Saarland',
    sn: 'Sachsen',
    st: 'Sachsen-Anhalt',
    sh: 'Schleswig-Holstein',
    th: 'Thüringen',
};
/** Short names in labels: „Grunderwerbsteuer 6,5 % (NRW)". */
export const propertyStateShortLabels = {
    bw: 'BW',
    by: 'BY',
    be: 'BE',
    bb: 'BB',
    hb: 'HB',
    hh: 'HH',
    he: 'HE',
    mv: 'MV',
    ni: 'NI',
    nw: 'NRW',
    rp: 'RP',
    sl: 'SL',
    sn: 'SN',
    st: 'ST',
    sh: 'SH',
    th: 'TH',
};
/** How `property.state` was set (pgEnum `property_state_source`): derived from the postal code or chosen by hand. */
export const PROPERTY_STATE_SOURCES = ['postal_code', 'manual'];
export const propertyStateSourceLabels = {
    postal_code: 'automatisch nach PLZ',
    manual: 'manuell',
};
/**
 * Land transfer tax rate per state in % with the day it applies from (rates as of 12.10.2026, checked by the
 * lead against the finanz-tools.de table; Bremen raised 5,0 → 5,5 % on 01.07.2025).
 */
export const LAND_TRANSFER_TAX_RATES = {
    bw: { pct: 5, validFrom: '2011-11-05' },
    by: { pct: 3.5, validFrom: '1997-01-01' },
    be: { pct: 6, validFrom: '2014-01-01' },
    bb: { pct: 6.5, validFrom: '2015-07-01' },
    hb: { pct: 5.5, validFrom: '2025-07-01' },
    hh: { pct: 5.5, validFrom: '2023-01-01' },
    he: { pct: 6, validFrom: '2014-08-01' },
    mv: { pct: 6, validFrom: '2019-07-01' },
    ni: { pct: 5, validFrom: '2014-01-01' },
    nw: { pct: 6.5, validFrom: '2015-01-01' },
    rp: { pct: 5, validFrom: '2012-03-01' },
    sl: { pct: 6.5, validFrom: '2015-01-01' },
    sn: { pct: 5.5, validFrom: '2023-01-01' },
    st: { pct: 5, validFrom: '2012-03-01' },
    sh: { pct: 6.5, validFrom: '2014-01-01' },
    th: { pct: 5, validFrom: '2024-01-01' },
};
/**
 * Rate used when neither an override nor a state is known (the former fixed Bavarian 3,5 %); every label then
 * says „Bundesland unbekannt" so the user sees that the figure is an assumption.
 */
export const LAND_TRANSFER_TAX_FALLBACK_PCT = 3.5;
/**
 * „Kaufnebenkosten" without the land transfer tax when a unit has no incidental cost items: notary 1,24 % + land
 * registry 0,76 % = 2,0 % as TeamProQ lists them. Never a broker commission — owner decision 30.10.2026 („Wir haben
 * nie Maklercourtage!!!", D-348; before: 5,07 % incl. 3,57 % commission). The investment calculator and the units
 * budget filter add it to the property's effective land transfer tax.
 */
export const OTHER_ACQUISITION_COSTS_PCT = 2.0;
/**
 * Incidental cost items that are a broker commission („Maklerprovision", „Courtage", „Provision"): the calculator
 * and the units budget filter ignore them (D-348).
 */
export const BROKER_COMMISSION_COST_ITEM = /makler|courtage|provision/i;
/** The same pattern for a Postgres case-insensitive regular expression (`~*`). */
export const BROKER_COMMISSION_COST_ITEM_SQL = 'makler|courtage|provision';
export const STATE_UNKNOWN_LABEL = 'Bundesland unbekannt';
let singleIndex = null;
function postalCodeIndex() {
    if (singleIndex !== null)
        return singleIndex;
    const index = new Map();
    for (const state of PROPERTY_STATES) {
        for (const code of POSTAL_CODES_BY_STATE[state].split(' '))
            if (code !== '')
                index.set(code, state);
    }
    singleIndex = index;
    return index;
}
function normalizeLocality(value) {
    return value
        .normalize('NFC')
        .toLowerCase()
        .replace(/\s*\(.*\)\s*$/, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/**
 * State of a German postal code; null for an unknown or malformed code. A code whose area spans several states
 * (27 of 8.163) is decided by the city name when it matches a locality of one state, else by the state with
 * most streets under that code.
 */
export function stateFromPostalCode(postalCode, city) {
    const code = postalCode?.trim() ?? '';
    if (!/^\d{5}$/.test(code))
        return null;
    const single = postalCodeIndex().get(code);
    if (single !== undefined)
        return single;
    const candidates = CROSS_STATE_POSTAL_CODES[code];
    if (candidates === undefined || candidates.length === 0)
        return null;
    const wanted = city === null || city === undefined ? '' : normalizeLocality(city);
    if (wanted !== '') {
        const match = candidates.find(([, localities]) => localities.some((locality) => {
            const normalized = normalizeLocality(locality);
            return normalized === wanted || wanted.startsWith(`${normalized} `);
        }));
        if (match !== undefined)
            return match[0];
    }
    return candidates[0]?.[0] ?? null;
}
/** Effective rate: the property's override, else its state's rate, else the fallback. */
export function effectiveLandTransferTax(property) {
    if (property.landTransferTaxPct !== null) {
        return { pct: property.landTransferTaxPct, source: 'manual', state: property.state };
    }
    if (property.state !== null) {
        return { pct: LAND_TRANSFER_TAX_RATES[property.state].pct, source: 'state', state: property.state };
    }
    return { pct: LAND_TRANSFER_TAX_FALLBACK_PCT, source: 'fallback', state: null };
}
export function effectiveLandTransferTaxPct(property) {
    return effectiveLandTransferTax(property).pct;
}
/** „Grunderwerbsteuer 6,5 % (NRW)", „Grunderwerbsteuer 3,5 % (Bundesland unbekannt)". */
export function landTransferTaxLabel(tax) {
    const where = tax.state === null ? STATE_UNKNOWN_LABEL : propertyStateShortLabels[tax.state];
    return `Grunderwerbsteuer ${formatPercent(tax.pct, 1)} (${where})`;
}
/** Source of the effective rate as Objektdaten shows it next to the rate. */
export const landTransferTaxSourceLabels = {
    manual: 'manuell',
    state: 'automatisch nach Bundesland',
    fallback: STATE_UNKNOWN_LABEL,
};
//# sourceMappingURL=land-transfer-tax.js.map